import '../../src/components/workspaces/compact-editor.css';
import './entity-identity-header.css';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUpRightIcon, DocumentDuplicateIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';

export const HEADER_ACTION_BUTTON_CLASS =
  'box-border h-8 min-h-8 rounded-md py-0 text-sm font-medium leading-none';

const shortenIdentifier = (value) => {
  const identifier = String(value || '');
  return identifier.length > 8 ? identifier.slice(0, 8) : identifier;
};

export function IdentifierButton({ value, label, onCopy }) {
  if (!value) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="h-6 max-w-24 px-1.5 font-mono text-[10px] font-normal text-ink-muted hover:bg-paper-muted hover:text-ink-secondary"
          onClick={(event) => {
            event.stopPropagation();
            onCopy(value, label);
          }}
          aria-label={`Copy ${label}`}
        >
          <span className="truncate">{shortenIdentifier(value)}</span>
          <DocumentDuplicateIcon className="ml-1 h-3 w-3 shrink-0" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <span className="font-mono text-xs">{value}</span>
        <span className="ml-2 text-xs">Click to copy</span>
      </TooltipContent>
    </Tooltip>
  );
}

export function HeaderCloseAction({ label = 'Close details', onClick, disabled = false }) {
  if (!onClick) return null;

  return (
    <span className="entity-close-action inline-flex shrink-0 items-center gap-2">
      <span className="entity-close-separator mx-0.5 h-5 w-px shrink-0 bg-edge" aria-hidden="true" />
      <Button
        type="button"
        variant="custom"
        size="icon"
        className={`${HEADER_ACTION_BUTTON_CLASS} w-8 shrink-0 border border-edge bg-white p-0 text-ink-secondary hover:bg-paper-muted hover:text-ink`}
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        title={label}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </Button>
    </span>
  );
}

export function EntityIdentityHeader({
  title,
  wrapTitle = false,
  identifier,
  identifierLabel,
  metadata = [],
  statusLabel,
  statusVariant = 'neutral',
  statuses = [],
  onCopyIdentifier,
  actions,
  children,
}) {
  const visibleMetadata = metadata.filter((item) => item?.value);
  const visibleStatuses =
    statuses.length > 0
      ? statuses.filter((status) => status?.label)
      : statusLabel
      ? [{ key: 'status', label: statusLabel, variant: statusVariant }]
      : [];

  const summaryMetadata = visibleMetadata.filter((item) => item.emphasis || item.summary);
  const secondaryMetadata = visibleMetadata.filter((item) => !item.emphasis && !item.summary);
  const renderMetadata = (item) => {
    const index = visibleMetadata.indexOf(item);
    return (
      <span
        key={item.key || index}
        className={cn('entity-metadata-item text-sm text-ink-muted', item.emphasis && 'font-medium text-ink')}
        style={{ '--metadata-order': index + 1 }}
      >
        {(identifier || index > 0) && <span className="entity-metadata-separator" aria-hidden="true">·</span>}
        <span className="entity-metadata-value">{item.value}</span>
      </span>
    );
  };

  const summary = (
    <div className={cn('entity-summary', summaryMetadata.length > 0 && 'entity-summary-primary')}>
      {summaryMetadata.map(renderMetadata)}
      {visibleStatuses.map((status, index) => (
        <span
          key={status.key || index}
          className="entity-metadata-item"
          style={{ '--metadata-order': visibleMetadata.length + index + 1 }}
        >
          {(identifier || visibleMetadata.length > 0 || index > 0) && (
            <span className="entity-metadata-separator" aria-hidden="true">·</span>
          )}
          <Badge variant={status.variant || 'neutral'}>{status.label}</Badge>
        </span>
      ))}
    </div>
  );

  return (
    <header className="compact-editor-identity-header entity-identity-header mx-3 mt-3 rounded-md border border-edge bg-paper-light px-2 py-5 md:mt-5 md:px-5 md:py-6">
      <div className="entity-identity-layout flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="entity-identity-main min-w-0">
          <h1 className={cn('entity-identity-title text-2xl font-semibold tracking-[-0.025em] text-ink md:text-[28px]', wrapTitle ? 'wrap-anywhere whitespace-normal' : 'truncate')}>
            {title}
          </h1>
          <div className="entity-metadata mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {summaryMetadata.length > 0 && summary}
            <div className="entity-secondary">{secondaryMetadata.map(renderMetadata)}</div>
            {summaryMetadata.length === 0 && summary}
            {identifier && <span className="entity-identifier">
              <IdentifierButton value={identifier} label={identifierLabel} onCopy={onCopyIdentifier} />
            </span>}
          </div>
        </div>
        {actions && <div className="entity-identity-actions flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export function RecordDetailsSection({ title, details = [], children, id }) {
  const headingId = id || `${String(title).toLowerCase().replace(/\s+/g, '-')}-heading`;

  return (
    <section className="mt-5 border-t border-edge pt-4" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-sm font-semibold text-ink">
        {title}
      </h2>
      {details.length > 0 && (
        <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {details.map((detail) => (
            <div key={detail.key || detail.label} className="min-w-0">
              <dt className="text-xs text-ink-muted">{detail.label}</dt>
              <dd
                className={cn(
                  'mt-1 break-words text-sm font-medium text-ink',
                  detail.mono && 'font-mono text-xs',
                )}
                title={typeof detail.value === 'string' ? detail.value : undefined}
              >
                {detail.value === null || detail.value === undefined || detail.value === ''
                  ? 'Not available'
                  : detail.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {children}
    </section>
  );
}

export function WorkspaceSectionCard({
  title,
  description,
  icon: Icon,
  children,
  className,
  bodyClassName,
  headerAction,
  headerMeta,
  enabled = true,
  onHeaderClick,
  hasErrors = false,
  expanded,
}) {
  if (!enabled) return children;

  const HeaderElement = onHeaderClick ? 'button' : 'div';

  return (
    <section className={cn('overflow-hidden rounded-lg bg-paper-light', className)}>
      <HeaderElement
        type={onHeaderClick ? 'button' : undefined}
        onClick={onHeaderClick}
        aria-expanded={onHeaderClick ? Boolean(expanded) : undefined}
        className={cn(
          'flex w-full items-start gap-2 rounded-t-lg border border-edge bg-white px-4 py-2.5 text-left',
          onHeaderClick &&
            'transition-colors hover:bg-paper-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-inset',
          hasErrors && 'border-status-red-text',
        )}
      >
        {Icon && (
          <div className="flex h-6 shrink-0 items-center">
            <Icon
              className={cn('h-4 w-4 text-ink-muted', hasErrors && 'text-status-red-text')}
              aria-hidden="true"
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex min-h-6 min-w-0 items-center gap-0.5">
            <h2
              className={cn(
                'min-w-0 text-sm font-semibold text-ink',
                hasErrors && 'text-status-red-text',
              )}
            >
              {title}
            </h2>
            {headerAction && <div className="shrink-0">{headerAction}</div>}
          </div>
          {description && <p className="mt-0.5 text-xs text-ink-muted">{description}</p>}
        </div>
        {headerMeta && <div className="ml-auto min-w-0">{headerMeta}</div>}
      </HeaderElement>
      <div
        className={cn(
          'rounded-b-lg border-x border-b border-edge bg-paper-light px-5 py-4',
          hasErrors && 'border-status-red-text',
          bodyClassName,
        )}
      >
        {children}
      </div>
    </section>
  );
}

export function RelationshipLinkAction({ label, onClick }) {
  if (!onClick) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-ink-muted hover:bg-paper-muted hover:text-ink-secondary"
          onClick={onClick}
          aria-label={label}
        >
          <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

export function Property({ label, value, mono = false, action }) {
  const visibleValue =
    value === null || value === undefined || value === '' ? 'Not available' : value;

  return (
    <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3 py-2.5 text-sm">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={cn('flex min-w-0 items-start gap-1.5 text-ink', mono && 'font-mono text-xs')}>
        <span className="min-w-0 flex-1 break-words">{visibleValue}</span>
        {action}
      </dd>
    </div>
  );
}

export function TimelineSection({ title, description, children, headerClassName }) {
  return (
    <section>
      <div className={cn('mb-3 flex items-center', headerClassName)}>
        <div>
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-ink-secondary">{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function EditorFieldGroup({
  title,
  description,
  icon,
  children,
  enabled,
  onHeaderClick,
  hasErrors,
  expanded,
}) {
  return (
    <WorkspaceSectionCard
      title={title}
      description={description}
      icon={icon}
      enabled={enabled}
      onHeaderClick={onHeaderClick}
      hasErrors={hasErrors}
      expanded={expanded}
    >
      <div className={enabled ? 'space-y-4' : undefined}>{children}</div>
    </WorkspaceSectionCard>
  );
}

export function EntityRecordSkeleton({ label = 'record' }) {
  return (
    <div
      className="h-full overflow-hidden bg-white"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={`Loading ${label}`}
    >
      <div className="mx-3 mt-3 motion-safe:animate-pulse rounded-md border border-edge bg-paper-light px-5 py-6 md:mt-5">
        <div className="flex items-start justify-between gap-5">
          <div className="space-y-3">
            <div className="h-7 w-48 rounded bg-paper-inset" />
            <div className="h-4 w-72 max-w-full rounded bg-paper-inset" />
          </div>
          <div className="flex gap-2">
            <div className="h-8 w-16 rounded-md bg-paper-inset" />
            <div className="h-8 w-16 rounded-md bg-paper-inset" />
          </div>
        </div>
        <div className="mt-5 border-t border-edge pt-4">
          <div className="h-4 w-32 rounded bg-paper-inset" />
          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="space-y-2">
                <div className="h-3 w-20 rounded bg-paper-inset" />
                <div className="h-4 w-28 rounded bg-paper-inset" />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid gap-6 px-3 pt-5 lg:grid-cols-[minmax(0,1fr)_290px]">
        <div className="space-y-6">
          <div className="h-4 w-36 motion-safe:animate-pulse rounded bg-paper-inset" />
          {[0, 1, 2, 3, 4].map((item) => (
            <div key={item} className="flex motion-safe:animate-pulse gap-4">
              <div className="h-6 w-6 shrink-0 rounded-full bg-paper-inset" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 rounded bg-paper-inset" />
                <div className="h-3 w-56 max-w-full rounded bg-paper-inset" />
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          {[0, 1, 2].map((item) => (
            <div
              key={item}
              className="h-40 motion-safe:animate-pulse rounded-lg border border-edge bg-paper-light"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
